import Path from '@leverege/path';
import { createSelectors } from '@leverege/ui-redux';
import { ValuesCache } from '@leverege/value-cache';
import equals from 'fast-deep-equal';
import Constants from '../Constants.js';
const initialState = {},
  cache = new ValuesCache({
    equals
  });
function addQueryName(str, queryName) {
  return `${str}${queryName ? `:${queryName}` : ''}`;
}

// eslint-disable-next-line default-param-last
function rsrc(state = initialState, action) {
  switch (action.type) {
    case Constants.RSRC_CREATE:
      return createRsrc(state, action);
    case Constants.RSRC_GET:
      return getRsrc(state, action);
    case Constants.RSRC_UPDATE:
      return updateRsrc(state, action);
    case Constants.RSRC_DELETE:
      return deleteRsrc(state, action);
    case Constants.RSRC_STATUS:
      return statusRsrc(state, action);
    default:
      return state;
  }
}
function createRsrc(state, action) {
  const {
    ownerId,
    coownerId
  } = action;
  const path = action?.path || '';
  const p = getBasePath(ownerId, coownerId);
  const toClean = Path(p).get(state, {});
  const toSet = {};
  Object.entries(toClean).forEach(([key, entry]) => {
    if (pathIsParent(key, path)) {
      // invalidate children
      toSet[key] = {
        status: entry?.status,
        value: entry?.value
      };
    } else {
      toSet[key] = entry;
    }
  });
  const nState = Path(p).copy(state, toSet);
  return getRsrc(nState, action);
}
function getRsrc(state, action) {
  // TODO : invalidate children
  const {
    rsrc,
    rsrcs,
    ownerId,
    coownerId,
    options,
    queryName
  } = action;
  const path = action?.path || '';
  const p = getBasePath(ownerId, coownerId);
  if (rsrcs) {
    // a list then
    const copy = {
      ...Path(p).get(state, {})
    };
    // write all the rsrc values
    rsrcs?.items?.forEach(rsrc => {
      copy[rsrc.path] = {
        ...copy[rsrc.path],
        [addQueryName('value', queryName)]: rsrc,
        exists: true
      };
    });
    // write the children
    const nValue = rsrcs?.items?.find(rsrc => rsrc.path === path) || copy[path]?.value;
    return Path(p).copy(state, {
      ...copy,
      [path]: {
        ...copy[path],
        value: nValue,
        [addQueryName('children', queryName)]: mergeChildren(rsrcs, copy[path]?.children),
        [addQueryName('childrenOptions', queryName)]: options,
        exists: !!nValue
      }
    });
  }
  const toCopy = Path(p).get(state, {});
  return Path(p).copy(state, {
    ...toCopy,
    [path]: {
      ...toCopy[path],
      [addQueryName('value', queryName)]: rsrc,
      [addQueryName('options', queryName)]: options,
      exists: !!rsrc
    }
  });
}
function updateRsrc(state, action) {
  const {
    rsrc,
    ownerId,
    coownerId
  } = action;
  const path = action?.path || '';
  let nState = state;
  if (path !== rsrc.path) {
    nState = deleteRsrc(nState, action);
  }
  return getRsrc(nState, {
    ...action,
    ownerId,
    coownerId,
    path: rsrc.path
  });
}
function deleteRsrc(state, action) {
  const {
    options,
    ownerId,
    coownerId
  } = action;
  const path = action?.path || '';
  const p = getBasePath(ownerId, coownerId);
  const toClean = Path(p).get(state, {});
  const toSet = {};
  Object.entries(toClean).forEach(([key, entry]) => {
    // if its the key that was deleted or we deleted children and the present path is a parent of the key
    if (key === path || options?.children === true && pathIsParent(path, key)) {
      // don't copy it, it was deleted
    } else if (pathIsParent(key, path)) {
      // remove entry from all parents children
      const nEntry = {};
      Object.entries(entry).forEach(([key, value]) => {
        if (key.startsWith('children') && Array.isArray(value?.items)) {
          const nItems = value.items.filter(vPath => vPath !== path);
          nEntry[key] = {
            ...value,
            items: nItems,
            total: nItems.length !== value.items.length ? value.total - 1 : value.total
          };
        } else {
          nEntry[key] = value;
        }
      });
      toSet[key] = nEntry;
    } else {
      toSet[key] = entry;
    }
  });
  return Path(p).copy(state, toSet);
}
function statusRsrc(state, action) {
  const {
    ownerId,
    coownerId,
    status,
    error,
    queryName
  } = action;
  const path = action?.path || '';
  const p = getBasePath(ownerId, coownerId);
  const toCopy = Path(p).get(state, {});
  return Path(p).copy(state, {
    ...toCopy,
    [path]: {
      ...toCopy[path],
      [addQueryName('status', queryName)]: status,
      [addQueryName('error', queryName)]: error === undefined ? toCopy[path]?.error : error
    }
  });
}
function getBasePath(ownerId, coownerId) {
  let path;
  if (coownerId != null) {
    path = `owner/${ownerId}/coowner/${coownerId}/rsrcs`;
  } else {
    path = `owner/${ownerId}/rsrcs`;
  }
  return path;
}
function mergeChildren(rsrcs, children) {
  const toReturn = {
    limit: rsrcs?.limit,
    offset: rsrcs?.offset,
    total: rsrcs?.total,
    items: []
  };
  for (let i = 0; i < rsrcs?.total; i++) {
    if (rsrcs?.items && rsrcs.offset != null && rsrcs.items[i - rsrcs.offset]) {
      toReturn.items.push(rsrcs.items[i - rsrcs.offset].path);
    } else {
      toReturn.items.push(children?.items && children?.items[i]?.path);
    }
  }
  return toReturn;
}
function pathIsParent(possibleParent, child) {
  if (possibleParent == null || child == null) {
    return false;
  }
  // empty string is parent to all
  return child.startsWith(`${possibleParent}/`) || possibleParent === '';
}
/* eslint-disable no-unused-vars */
function pathIsSibling(p1, p2) {
  return !pathIsParent(p1, p2) && !pathIsParent(p2, p1) && !(p1 === p2);
}
/* eslint-enable no-unused-vars */

// stolen from allowed reducer

function get(state, ownerId, coownerId, path, reduxOpts = {}) {
  const key = addQueryName('value', reduxOpts?.queryName);
  return Path(getBasePath(ownerId, coownerId)).get(state, {})[path]?.[key];
}
function calcList(basePath, list, queryName) {
  if (!list) {
    return list;
  }
  const valueKey = addQueryName('value', queryName);
  return {
    ...list,
    items: list?.items?.map(i => basePath[i]?.[valueKey])
  };
}
function list(state, ownerId, coownerId, path, reduxOpts) {
  const bPath = getBasePath(ownerId, coownerId);
  const basePath = Path(bPath).get(state, {});
  const childrenKey = addQueryName('children', reduxOpts?.queryName);
  const list = basePath[path || '']?.[childrenKey];
  return cache.calc(`${bPath}/${path || ''}.${childrenKey}`, calcList, basePath, list, reduxOpts?.queryName);
}
function calcStatus(node, queryName) {
  const nNode = {
    status: node?.[addQueryName('status', queryName)],
    error: node?.[addQueryName('error', queryName)],
    exists: node?.exists
  };
  return nNode;
}
function status(state, ownerId, coownerId, path, reduxOpts) {
  const bPath = getBasePath(ownerId, coownerId);
  const node = Path(bPath).get(state, {})[path || ''];
  if (!node) {
    return null;
  }
  // TODO: memoize
  const key = addQueryName('status', reduxOpts?.queryName);
  return cache.calc(`${bPath}/${path || ''}.${key}`, calcStatus, node, reduxOpts?.queryName);
}
rsrc.get = get;
rsrc.list = list;
rsrc.status = status;
rsrc.createSelectors = (key, createSetStateKey = false) => {
  return createSelectors(key, {
    get,
    list,
    status
  }, createSetStateKey);
};

// Set up initial defaults to make methods available immediately with default state key
const selectors = rsrc.createSelectors('rsrcs', true);
Object.keys(selectors).forEach(key => {
  rsrc[key] = selectors[key];
});
export default rsrc;